import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle, Facebook, Instagram, ExternalLink, AlertTriangle } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent! We\'ll review your submission and respond soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsSubmitted(false), 5000);
    }
  };

  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-stone-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
              Contact <span className="text-teal-600">XRU</span>
            </h1>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              Report a civil or human rights violation, become a member, or learn how you can 
              support our mission. We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Report Violation Banner */}
      <section className="py-8 bg-red-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <AlertTriangle className="text-white" size={32} />
              <div>
                <p className="text-white font-bold text-lg">Need to Report a Violation?</p>
                <p className="text-white/90">Use the form below and select "Report a Violation" as your subject.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-stone-800 mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name-input"
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-stone-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    data-testid="contact-subject-select"
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300"
                  >
                    <option value="">Select a topic</option>
                    <option value="violation">Report a Violation</option>
                    <option value="membership">Membership Inquiry</option>
                    <option value="volunteer">Volunteer Opportunities</option>
                    <option value="donate">Donation Information</option>
                    <option value="media">Media/Press Inquiry</option>
                    <option value="partnership">Partnership Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="contact-message-textarea"
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300 resize-none"
                    placeholder="Please provide as much detail as possible..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting || isSubmitted}
                  data-testid="contact-submit-btn"
                  className="w-full px-8 py-4 bg-teal-600 hover:bg-teal-700 disabled:bg-stone-300 text-white disabled:text-stone-500 font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/25 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <span className="animate-spin">⏳</span>
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle size={20} />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
            
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-stone-800 mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {/* Email */}
                <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-stone-800 mb-1">Email Us</h3>
                      <a 
                        href={`mailto:${siteConfig.email}`}
                        className="text-teal-600 hover:text-teal-700 transition-colors break-all"
                      >
                        {siteConfig.email}
                      </a>
                      <p className="text-stone-500 text-sm mt-1">We respond within 48 hours</p>
                    </div>
                  </div>
                </div>
                
                {/* Location */}
                <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-stone-800 mb-1">Location</h3>
                      <p className="text-stone-700">{siteConfig.location}</p>
                      <p className="text-stone-500 text-sm mt-1">Serving communities nationwide</p>
                    </div>
                  </div>
                </div>
                
                {/* Social Media */}
                <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl">
                  <h3 className="text-lg font-semibold text-stone-800 mb-4">Follow Us</h3>
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={siteConfig.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 hover:bg-teal-600 text-stone-700 hover:text-white rounded-lg transition-all duration-300 group"
                    >
                      <Facebook size={20} />
                      <span className="font-medium">Facebook</span>
                      <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                    <a
                      href={siteConfig.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 hover:bg-teal-600 text-stone-700 hover:text-white rounded-lg transition-all duration-300 group"
                    >
                      <Instagram size={20} />
                      <span className="font-medium">Instagram</span>
                      <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </div>
                </div>

                {/* Urgent Matters */}
                <div className="p-6 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="text-red-500 flex-shrink-0" size={24} />
                    <div>
                      <h3 className="text-lg font-semibold text-red-600 mb-2">Urgent Matters</h3>
                      <p className="text-stone-600 text-sm">
                        If you are experiencing an emergency or immediate threat, please contact 
                        local authorities. For urgent civil or human rights matters, email us with 
                        "URGENT" in the subject line.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              Frequently Asked <span className="text-teal-600">Questions</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                q: 'How do I report a civil or human rights violation?',
                a: 'Use our contact form and select "Report a Violation" as your subject. Provide as much detail as possible about the incident, including dates, locations, and any documentation you have. We will review your submission and follow up within 48 hours.'
              },
              {
                q: 'How can I become a member of XRU?',
                a: 'Contact us through the form and select "Membership Inquiry." We\'ll provide information about membership benefits, dues, and how you can get involved in our work.'
              },
              {
                q: 'Does XRU provide legal representation?',
                a: 'XRU advocates for civil and human rights through various means including legal action. While we don\'t provide individual legal representation, we work with partner organizations and may pursue cases that address systemic issues affecting our community.'
              },
              {
                q: 'How can I support XRU\'s work?',
                a: 'You can support us by becoming a member, making a donation, volunteering your time, or spreading awareness about our mission. Every contribution helps us fight for justice.'
              }
            ].map((item, index) => (
              <div
                key={index}
                className="p-6 bg-white border border-stone-200 rounded-xl shadow-sm"
              >
                <h3 className="text-lg font-semibold text-stone-800 mb-2">{item.q}</h3>
                <p className="text-stone-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
